import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { EMPTY, Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import swal from 'sweetalert';
import { ApiService } from './api.service';
import { authLocalStorageToken, LOGIN_PATH_SELECT_ACCOUNT, odooInfo, recoverAttemptKey, saveReturnUrl } from '../shared/common/helper';

/**
 * @description Service phục hồi phiên đăng nhập, dùng chung cho toàn ứng dụng.
 * @description Trước đây logic này bị nhân bản ở InterceptorService và SSOMsalService với
 * @description hai cờ chống trùng RIÊNG BIỆT, nên hai luồng có thể cùng hiện popup và cùng
 * @description gọi loginRedirect. Gom về đây với providedIn root để đảm bảo chỉ có một
 * @description instance duy nhất, tức một cờ và một bộ đếm duy nhất.
 */
@Injectable({ providedIn: 'root' })
export class AuthRecoveryService {

  /**
   * @description Thời gian tối đa chờ MSAL về trạng thái InteractionStatus.None (ms).
   * @description inProgress$ là stream vô hạn: nếu một luồng tương tác trước đó bị kẹt
   * @description (iframe treo, handleRedirectPromise không resolve) thì trạng thái None
   * @description KHÔNG BAO GIỜ phát ra và nơi đang chờ sẽ treo vĩnh viễn.
   * @description Đặt 15s vì lúc khởi động MSAL có thể bận khá lâu, nhưng vượt ngưỡng
   * @description này thì chắc chắn là kẹt.
   */
  readonly msalIdleTimeoutMs = 15000;

  /**
   * @description Cờ chống gọi trùng: nhiều request cùng fail một lúc thì chỉ lần đầu
   * @description được hiện popup và redirect.
   */
  private isRecovering = false;

  /**
   * @description Key đếm số lần đã thử phục hồi, lưu ở sessionStorage để tự xoá khi đóng tab.
   * @description Khai báo ở helper để luồng đăng xuất dọn đúng key này thay vì
   * @description sessionStorage.clear() - clear() xoá lây dữ liệu của ứng dụng khác cùng origin.
   */
  private readonly recoverAttemptKey = recoverAttemptKey;

  /**
   * @description Số lần phục hồi tối đa trước khi dừng hẳn, chống vòng lặp đăng nhập
   * @description vô tận khi nguyên nhân không nằm ở token.
   */
  private readonly maxRecoverAttempts = 2;

  constructor(
    private api: ApiService,
    private translate: TranslateService,
    private authMsalService: MsalService,
  ) { }

  /**
   * @description Phục hồi phiên đăng nhập.
   * @description LƯU Ý: observable trả về là cold - nơi gọi BẮT BUỘC phải subscribe,
   * @description nếu không thì popup sẽ không bao giờ hiện.
   * @param error: any lỗi dẫn tới việc phải phục hồi
   * @return Observable
   */
  recover(error: any): Observable<never> {
    console.error('[Auth] Recovering authentication by interactive login.', {
      name: error?.name,
      errorCode: error?.errorCode,
      subError: error?.subError,
      message: error?.message,
    });

    saveReturnUrl(`${window.location.pathname}${window.location.search}`);

    this.api.loadingOff();
    this.api.loadingCustomOff();

    if (this.isRecovering) {
      return EMPTY;
    }
    this.isRecovering = true;

    return this.executeRecovery();
  }

  /**
   * @description Xoá bộ đếm phục hồi, gọi khi có request chạy thành công.
   * @return void
   */
  resetRecoverAttempts(): void {
    sessionStorage.removeItem(this.recoverAttemptKey);
  }

  /**
   * @description Thực hiện phục hồi phiên.
   * @return Observable
   */
  private executeRecovery(): Observable<never> {
    /**
     * Chống vòng lặp đăng nhập: đã phục hồi quá số lần cho phép mà vẫn fail nghĩa là
     * nguyên nhân không nằm ở token.
     */
    const attempts = this.increaseRecoverAttempts();
    if (attempts > this.maxRecoverAttempts) {
      return this.showUnrecoverableError(attempts);
    }

    localStorage.removeItem(authLocalStorageToken);
    localStorage.removeItem(odooInfo);

    return from(swal({
      icon: 'warning',
      title: this.translate.instant('COMMON.msg.expires'),
      buttons: { ok: this.translate.instant('COMMON.btn.close') },
      closeOnClickOutside: false,
      closeOnEsc: false,
    } as any)).pipe(
      switchMap(() => {
        /**
         * Không gọi logout() của MSAL vì việc đó huỷ luôn session SSO của Azure AD,
         * khiến người dùng phải nhập lại mật khẩu. loginRedirect() là đủ - AAD sẽ tự
         * cấp token mới nếu session còn hiệu lực.
         */
        this.authMsalService.loginRedirect({ scopes: ['openid', 'user.read'] }).subscribe({
          error: (redirectError) => {
            this.isRecovering = false;
            console.error('[Auth] loginRedirect thất bại khi đang phục hồi phiên.', redirectError);
            this.showRedirectFailedError();
          }
        });
        return EMPTY;
      })
    );
  }

  /**
   * @description Tăng bộ đếm số lần phục hồi và trả về giá trị mới.
   * @return number
   */
  private increaseRecoverAttempts(): number {
    const current = Number(sessionStorage.getItem(this.recoverAttemptKey)) || 0;
    const next = current + 1;
    sessionStorage.setItem(this.recoverAttemptKey, String(next));
    return next;
  }

  /**
   * @description Báo lỗi khi đã thử phục hồi quá số lần cho phép mà vẫn thất bại.
   * @description BẮT BUỘC phải cho người dùng một hành động thoát ra. Popup này là ngõ cụt
   * @description thật sự: sau khi đóng thì cờ isRecovering vẫn bật nên mọi request sau đều
   * @description trả EMPTY im lặng, và bấm F5 cũng vô ích vì bộ đếm nằm ở sessionStorage
   * @description (sống sót qua reload, chỉ mất khi đóng tab). Nếu chỉ có nút Đóng thì cách
   * @description thoát duy nhất là đóng hẳn tab - người dùng không thể tự đoán ra.
   * @param attempts: number số lần đã thử
   * @return Observable
   */
  private showUnrecoverableError(attempts: number): Observable<never> {
    console.error('[Auth] Vượt quá số lần phục hồi cho phép, dừng vòng lặp đăng nhập.', {
      attempts,
      maxRecoverAttempts: this.maxRecoverAttempts,
    });

    return from(swal({
      icon: 'error',
      title: 'Không thể đăng nhập',
      text: 'Hệ thống đã thử đăng nhập lại nhiều lần nhưng không thành công.\n\n'
        + 'Bấm "Thử lại" để tải lại trang, hoặc "Đăng xuất" để đăng nhập bằng tài khoản khác. '
        + 'Nếu vẫn không được, vui lòng liên hệ bộ phận IT.',
      buttons: {
        logout: { text: 'Đăng xuất', value: 'logout' },
        retry: { text: 'Thử lại', value: 'retry' },
      },
      closeOnClickOutside: false,
      closeOnEsc: false,
    } as any)).pipe(
      switchMap((choice: any) => {
        if (choice === 'logout') {
          this.forceLogout();
        } else {
          // Mặc định là thử lại - luôn an toàn hơn để người dùng mắc kẹt
          this.retryFromScratch();
        }
        return EMPTY;
      })
    );
  }

  /**
   * @description Đường thoát chính: xoá bộ đếm, xoá cờ interaction kẹt rồi tải lại trang.
   * @description Phải xoá bộ đếm bằng tay ở đây vì nó nằm ở sessionStorage nên sống sót
   * @description qua reload - không xoá thì người dùng bấm F5 bao nhiêu lần cũng rơi lại
   * @description đúng popup này.
   * @return void
   */
  private retryFromScratch(): void {
    this.resetRecoverAttempts();
    this.clearStuckMsalInteraction();
    window.location.reload();
  }

  /**
   * @description Kết thúc phiên tại portal để người dùng đăng nhập bằng tài khoản khác.
   * @description Dùng logoutLocal() chứ KHÔNG dùng logout(): đây là luồng xử lý lỗi, không
   * @description được phép huỷ session SSO của Azure AD vì sẽ đăng xuất luôn các ứng dụng
   * @description khác dùng chung tenant.
   * @description Điều hướng kèm selectAccount vì đăng xuất cục bộ giữ nguyên session SSO,
   * @description không có cờ này thì lần đăng nhập sau tự vào lại đúng tài khoản đang lỗi.
   * @description KHÔNG inject AuthService để gọi logout vì sẽ tạo vòng phụ thuộc:
   * @description AuthRecoveryService -> AuthService -> (constructor gọi HTTP)
   * @description -> InterceptorService -> AuthRecoveryService.
   * @return void
   */
  private forceLogout(): void {
    const account = this.authMsalService.instance.getActiveAccount();
    this.api.logoutLocal(this.authMsalService, account, LOGIN_PATH_SELECT_ACCOUNT);
  }

  /**
   * @description Xử lý khi chính loginRedirect cũng thất bại.
   * @description Thường do MSAL còn kẹt cờ interaction từ lần đăng nhập trước bị huỷ giữa chừng.
   * @return void
   */
  private showRedirectFailedError(): void {
    swal({
      icon: 'error',
      title: 'Không mở được trang đăng nhập',
      text: 'Trang sẽ được tải lại để thử lại. Nếu vẫn không được, vui lòng đóng hẳn trình duyệt rồi mở lại.',
      buttons: { ok: this.translate.instant('COMMON.btn.close') },
      closeOnClickOutside: false,
      closeOnEsc: false,
    } as any).then(() => {
      this.clearStuckMsalInteraction();
      window.location.reload();
    });
  }

  /**
   * @description Xoá cờ "đang có tương tác" còn sót của MSAL.
   * @description MSAL lưu key msal.interaction.status khi bắt đầu login/redirect và chỉ xoá
   * @description khi luồng kết thúc. Nếu luồng bị huỷ giữa chừng (đóng tab, mất mạng) thì key
   * @description này còn lại vĩnh viễn, làm mọi lần loginRedirect sau đều ném interaction_in_progress.
   * @description Config đang bật storeAuthStateInCookie nên phải xoá cả cookie.
   * @return void
   */
  private clearStuckMsalInteraction(): void {
    const interactionStatusKey = 'msal.interaction.status';

    sessionStorage.removeItem(interactionStatusKey);
    localStorage.removeItem(interactionStatusKey);
    document.cookie = `${interactionStatusKey}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

    console.info('[Auth] Đã xoá cờ interaction bị kẹt trước khi tải lại trang.');
  }
}
